import { useEffect, useRef, useState } from 'react';
import { sprekersApi } from '../api';
import type { SessionEvent } from '../types';

export type SseConnectionStatus = 'connecting' | 'open' | 'reconnecting' | 'error' | 'stopped';

/** Stilte (geen enkel bericht, ping inbegrepen) waarna de verbinding als dood
 * geldt. Moet ruim boven backend/sprekers/realtime.py HEARTBEAT_SECONDS (10 s)
 * liggen: twee gemiste pings plus marge. */
export const WATCHDOG_MS = 25_000;
/** Opeenvolgende mislukte (her)verbindingspogingen zonder één ontvangen
 * bericht voordat we automatisch opgeven ('stopped'). */
export const MAX_RECONNECT_ATTEMPTS = 6;
const BACKOFF_BASE_MS = 1_000;
const BACKOFF_MAX_MS = 15_000;
/** Maximaal aantal door een gat/ping afgedwongen heropeningen per tijdvenster
 * -- beschermt tegen een lus als de snapshot het gat niet kan dichten. */
export const MAX_RESYNCS_PER_WINDOW = 3;
const RESYNC_WINDOW_MS = 60_000;

export interface SessionEventsOptions {
  /** Geeft true als dit event een gemiste update verraadt (gat in de
   * state_version-reeks, of een ping met een nieuwere versie). De verbinding
   * wordt dan heropend om de actuele `presentation.state`-snapshot op te
   * halen. Het event zelf wordt wel eerst aan `onEvent` aangeboden. */
  needsResync?: (event: SessionEvent) => boolean;
  /** Verhoog om na 'stopped' handmatig opnieuw te beginnen (nieuwe teller). */
  retryKey?: number;
  watchdogMs?: number;
}

/**
 * Abonneert op de SSE-stream van een live sessie (GET .../sessions/{id}/events,
 * backend/sprekers/realtime.py) met een los, sessiegebonden stream-token
 * (nooit het gewone gebruikers-JWT, zie backend/sprekers/service.py
 * create_stream_token/verify_stream_token).
 *
 * De browser's EventSource herverbindt zelf bij een tijdelijke
 * verbindingsonderbreking (sectie 17.3). Bij een fatale afwijzing (bijv. een
 * verlopen of ingetrokken stream-token, HTTP 401) stopt de browser vanzelf met
 * herverbinden en gaat readyState naar CLOSED; dat onderscheid gebruiken we
 * om 'reconnecting' (tijdelijk, browser probeert opnieuw) te scheiden van
 * 'error' (definitief, de aanroeper moet zelf een nieuw token/sessie regelen).
 *
 * Wat de browser NIET ziet is een stil gestorven verbinding (bijv. een
 * half-open TCP-verbinding na een wifi-uitval): er komt dan geen onerror. De
 * server stuurt daarom bij stilte elke 10 s een `presentation.ping`, en een
 * watchdog hier heropent de verbinding als er WATCHDOG_MS lang niets binnenkomt
 * -- met exponentiële backoff en een harde bovengrens (MAX_RECONNECT_ATTEMPTS
 * opeenvolgende mislukkingen, daarna 'stopped'). Elke nieuwe verbinding begint
 * met een snapshot van de serverstatus, dus gemiste dia-wissels worden bij het
 * heropenen vanzelf ingehaald.
 *
 * `onEvent` mag elke render een nieuwe functie-identiteit hebben (via een ref
 * bijgehouden); alleen sessionId/streamToken/retryKey wijzigen laten de
 * verbinding opnieuw opzetten -- en telkens wordt eerst de vorige EventSource
 * gesloten voordat een nieuwe wordt geopend, dus er bestaan nooit twee
 * tegelijk.
 */
export default function useSessionEvents(
  sessionId: string | null | undefined,
  streamToken: string | null | undefined,
  onEvent: (event: SessionEvent) => void,
  options: SessionEventsOptions = {},
): SseConnectionStatus {
  const [status, setStatus] = useState<SseConnectionStatus>('connecting');
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const needsResyncRef = useRef(options.needsResync);
  needsResyncRef.current = options.needsResync;
  const watchdogMs = options.watchdogMs ?? WATCHDOG_MS;
  const retryKey = options.retryKey ?? 0;

  useEffect(() => {
    if (!sessionId || !streamToken) {
      setStatus('connecting');
      return undefined;
    }

    const url = sprekersApi.sessionEventsUrl(sessionId, streamToken);
    let source: EventSource | null = null;
    let watchdogTimer: ReturnType<typeof setTimeout> | undefined;
    let reopenTimer: ReturnType<typeof setTimeout> | undefined;
    let failedAttempts = 0; // opeenvolgend, gereset door elk ontvangen bericht
    let resyncTimes: number[] = [];
    let everConnected = false;

    const clearTimers = () => {
      if (watchdogTimer) clearTimeout(watchdogTimer);
      if (reopenTimer) clearTimeout(reopenTimer);
      watchdogTimer = undefined;
      reopenTimer = undefined;
    };

    const closeSource = () => {
      if (!source) return;
      source.onopen = null;
      source.onmessage = null;
      source.onerror = null;
      source.close();
      source = null;
    };

    const stop = () => {
      clearTimers();
      closeSource();
      setStatus('stopped');
    };

    const scheduleReopen = (delayMs: number) => {
      clearTimers();
      closeSource();
      setStatus('reconnecting');
      reopenTimer = setTimeout(open, delayMs);
    };

    const handleStall = () => {
      failedAttempts += 1;
      if (failedAttempts > MAX_RECONNECT_ATTEMPTS) {
        stop();
        return;
      }
      scheduleReopen(Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** (failedAttempts - 1)));
    };

    const armWatchdog = () => {
      if (watchdogTimer) clearTimeout(watchdogTimer);
      watchdogTimer = setTimeout(handleStall, watchdogMs);
    };

    const requestResync = () => {
      const now = Date.now();
      resyncTimes = [...resyncTimes.filter((t) => now - t < RESYNC_WINDOW_MS), now];
      if (resyncTimes.length > MAX_RESYNCS_PER_WINDOW) {
        stop();
        return;
      }
      scheduleReopen(0);
    };

    function open() {
      closeSource();
      setStatus(everConnected ? 'reconnecting' : 'connecting');
      const es = new EventSource(url);
      source = es;

      es.onopen = () => {
        everConnected = true;
        setStatus('open');
        armWatchdog();
      };
      es.onmessage = (message) => {
        failedAttempts = 0;
        armWatchdog();
        let event: SessionEvent;
        try {
          event = JSON.parse(message.data);
        } catch {
          return; // onverwachte payload negeren
        }
        onEventRef.current(event);
        if (needsResyncRef.current?.(event)) requestResync();
      };
      es.onerror = () => {
        // readyState is pas CLOSED als de browser de verbinding definitief heeft
        // opgegeven (bijv. een niet-200-respons zoals een afgewezen stream-token
        // -- de EventSource-spec herverbindt dan bewust niet meer). Zolang de
        // browser nog zelf blijft proberen (readyState CONNECTING) is dit een
        // tijdelijke onderbreking; de watchdog vangt het geval af dat die
        // pogingen stil blijven mislukken.
        if (es.readyState === EventSource.CLOSED) {
          clearTimers();
          setStatus('error');
        } else {
          setStatus('reconnecting');
        }
      };
      armWatchdog(); // dekt ook een verbinding die nooit opent
    }

    open();

    return () => {
      clearTimers();
      closeSource();
    };
  }, [sessionId, streamToken, retryKey, watchdogMs]);

  return status;
}
